import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { EmptyFavoritesMessage } from 'features/favorites';
import { GameCard } from 'features/games';
import type IGameSettings from 'types/models/IGameSettings';

import { useOpenType } from 'core/providers/OpenTypeContext';
import { GAME_CARD_ASPECT_RATIO, GRID_GAP } from '../../../constants';
import { GenericErrorBoundary } from '../ErrorBoundaries/GenericErrorBoundary';
import { GameCardFallback } from '../ErrorBoundaries/GameCardErrorBoundary/GameCardFallback';

import styles from './ReactVirtualVerticalGrid.module.css';

/**
 * Rows kept mounted above and below the visible range.
 *
 * The grid used to keep every row it had ever shown, so the mounted set grew with how far
 * the user had scrolled rather than with what fits on screen - and every card in those
 * rows holds a decoded thumbnail, which is what actually fills memory. Windowing restores
 * a constant ceiling; this buffer keeps the scroll-back the growth was paying for, and
 * covers the beat the virtualizer's range lags behind a fling on iOS.
 */
const ROW_OVERSCAN = 4;

interface ReactVirtualVerticalGridProps {
  games: IGameSettings[];
  isFav: boolean;
  sectionName: string;
  hasJackpotPanel: boolean;
  ref?: React.Ref<HTMLDivElement>;
  /** Ref to the scroll container (lobbyContainer) - required for virtualization to work properly */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

interface GameItemProps {
  game: IGameSettings;
  index: number;
  sectionName: string;
  games: IGameSettings[];
}

// Individual game item component
const GameItem = memo(({ game, index, sectionName, games }: GameItemProps) => {
  if (!game) return null;

  return (
    <GenericErrorBoundary
      type="game"
      identifier={`GameCard-${game.gameKey}`}
      fallback={(error, reset) => <GameCardFallback gameName={game.name} error={error} onRetry={reset} />}
    >
      <GameCard data={game} sectionName={sectionName} displayIndex={index + 1} sectionGames={games} />
    </GenericErrorBoundary>
  );
});

GameItem.displayName = 'GameItem';

// Row component that renders multiple games in a row
const VirtualRow = memo(
  ({
    rowIndex,
    columnsCount,
    games,
    sectionName,
    style,
  }: {
    rowIndex: number;
    columnsCount: number;
    games: IGameSettings[];
    sectionName: string;
    style: React.CSSProperties;
  }) => {
    const startIndex = rowIndex * columnsCount;
    const rowGames = games.slice(startIndex, startIndex + columnsCount);

    return (
      <div style={style} className={styles.virtualRow}>
        {rowGames.map((game, colIndex) => (
          <div key={`${game.gameKey}_${startIndex + colIndex}`} className={styles.gameCell}>
            <GameItem game={game} index={startIndex + colIndex} sectionName={sectionName} games={games} />
          </div>
        ))}
        {/* Fill empty cells in last row to maintain grid alignment */}
        {rowGames.length < columnsCount &&
          Array.from({ length: columnsCount - rowGames.length }).map((_, i) => (
            <div key={`empty-${i}`} className={styles.gameCell} />
          ))}
      </div>
    );
  },
);

VirtualRow.displayName = 'VirtualRow';

const ReactVirtualVerticalGrid = ({
  games,
  isFav,
  sectionName,
  hasJackpotPanel,
  scrollContainerRef,
  ref,
}: ReactVirtualVerticalGridProps) => {
  const { openType } = useOpenType();

  // Note: scrollContainerRef is expected to be provided (lobbyContainerRef from Section.tsx)
  // The virtualizer needs this to track scroll position of the parent container
  const scrollElementRef = scrollContainerRef;

  // Calculate number of columns based on viewport width (matching GameGrid.module.css breakpoints)
  // Note: scrollElementRef is a stable ref object, so listing it in the dependency arrays
  // below never retriggers anything. The ref's .current value is read when the callback is
  // invoked, which happens on resize events.
  const getColumnsCount = useCallback(() => {
    if (typeof window === 'undefined') return 2;

    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 1600) {
      // auto-fill with minmax(220px, 1fr) - calculate based on container width
      const containerWidth = scrollElementRef?.current?.offsetWidth || viewportWidth;
      const padding = 40; // 20px on each side at desktop
      const gap = GRID_GAP.desktop;
      const minCardWidth = 220;
      const availableWidth = containerWidth - padding;
      return Math.max(2, Math.floor((availableWidth + gap) / (minCardWidth + gap)));
    }
    if (viewportWidth >= 1280) return 6;
    if (viewportWidth >= 1026) return 5;
    if (viewportWidth >= 576) return 4;
    if (viewportWidth >= 389) return 3;
    return 2;
  }, [scrollElementRef]);

  // Get gap value based on viewport
  const getGap = useCallback(() => {
    if (typeof window === 'undefined') return 8;
    // Match the gap values from GameGrid.module.css
    const viewportWidth = window.innerWidth;
    return viewportWidth >= 1025 ? GRID_GAP.desktop : GRID_GAP.mobile;
  }, []);

  // Calculate row height based on viewport width (card aspect ratio ~1.378)
  const getRowHeight = useCallback(() => {
    if (typeof window === 'undefined') return 200;

    const viewportWidth = window.innerWidth;
    const gap = getGap();
    const columnsCount = getColumnsCount();

    // Calculate card width based on container width and columns
    const padding = viewportWidth >= 1025 ? 40 : 16; // 20px or 8px on each side
    const containerWidth = scrollElementRef?.current?.offsetWidth || viewportWidth;
    const availableWidth = containerWidth - padding;
    const cardWidth = (availableWidth - gap * (columnsCount - 1)) / columnsCount;

    // Card aspect ratio is 1.37804878 (height/width)
    const cardHeight = cardWidth * GAME_CARD_ASPECT_RATIO;

    // Add gap to row height
    return Math.ceil(cardHeight + gap);
  }, [getColumnsCount, getGap, scrollElementRef]);

  // Get padding values
  const getPadding = useCallback(() => {
    if (typeof window === 'undefined') return { horizontal: 8, top: 0, bottom: 8 };
    const viewportWidth = window.innerWidth;
    const isFullscreen = openType === 'fullscreen';

    const horizontal = viewportWidth >= 1025 ? 20 : 8;
    const bottom = isFullscreen && viewportWidth >= 1025 ? 40 : isFullscreen ? 17 : 8;

    return { horizontal, top: 0, bottom };
  }, [openType]);

  const [columnsCount, setColumnsCount] = useState(getColumnsCount);
  const [rowHeight, setRowHeight] = useState(getRowHeight);

  // Calculate total rows needed
  const rowsCount = useMemo(() => {
    return Math.ceil(games.length / columnsCount);
  }, [games.length, columnsCount]);

  // Monitor window resize for responsive layout (debounced)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const updateDimensions = () => {
      setColumnsCount(getColumnsCount());
      setRowHeight(getRowHeight());
    };

    const debouncedResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 150);
    };

    // Initial calculation after a brief delay to ensure container is mounted
    const timeoutId = setTimeout(updateDimensions, 0);

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [getColumnsCount, getRowHeight]);

  // Initialize virtualizer for vertical scrolling
  const virtualizer = useVirtualizer({
    count: rowsCount,
    getScrollElement: () => scrollElementRef?.current ?? null,
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan: ROW_OVERSCAN,
  });

  // Force virtualizer to recalculate when row height changes
  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, columnsCount, virtualizer]);

  // Handle empty games case
  if (games.length === 0) {
    return isFav ? (
      <div
        className={styles.emptyContainer}
        data-has-jackpot={hasJackpotPanel}
        data-section-name={sectionName}
        data-is-favorites="true"
        open-type={openType}
        ref={ref}
      >
        <EmptyFavoritesMessage />
      </div>
    ) : null;
  }

  const padding = getPadding();

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div
      className={styles.virtualizedContainer}
      data-has-jackpot={hasJackpotPanel}
      data-section-name={sectionName}
      open-type={openType}
      ref={ref}
      style={{
        paddingLeft: padding.horizontal,
        paddingRight: padding.horizontal,
        paddingBottom: padding.bottom,
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => (
          <VirtualRow
            key={virtualRow.index}
            rowIndex={virtualRow.index}
            columnsCount={columnsCount}
            games={games}
            sectionName={sectionName}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: virtualRow.size,
              transform: `translateY(${virtualRow.start}px)`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

ReactVirtualVerticalGrid.displayName = 'ReactVirtualVerticalGrid';

export default memo(ReactVirtualVerticalGrid);
